import React from 'react';
import PropTypes from 'prop-types';
import { LuListFilter, LuSearch, LuX } from 'react-icons/lu';
import Button from '../ui/Button';
import SegmentedControl from '../ui/SegmentedControl';
import { GLASS_FIELD } from '../ui/glass';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/LanguageContext';
import ColumnMenu from './ColumnMenu';

const CHIP_STATES = {
  applied: 'tw-border-teal-500/25 tw-bg-teal-500/10 tw-text-teal-800 dark:tw-border-teal-400/20 dark:tw-bg-teal-400/10 dark:tw-text-teal-200',
  pending: 'tw-border-dashed tw-border-amber-500/40 tw-bg-amber-400/10 tw-text-amber-800 dark:tw-border-amber-400/30 dark:tw-text-amber-200',
};

/**
 * Search, status switch, column chooser and the active column filters of a
 * data page. `compact` (the card list on narrow screens): stacked, and a
 * "Filtreler" button opens the column filters, since there are no headers.
 * `chips`: [{ key, label, value, state }] for the column filters in use.
 * `extra`: page-specific quick filters (e.g. ToggleChips) on a row of their own.
 * `search.minLength`: characters before the search applies (default 3; 1 hides the hint).
 */
const DataToolbar = ({ search, status, columnMenu, compact, onOpenFilters, chips, onRemoveChip, onClearAll, extra }) => {
  const { t } = useTranslation();
  const isWide = useMediaQuery('(min-width: 640px)');
  const minLength = search.minLength ?? 3;
  const searchTooShort = search.value.length > 0 && search.value.length < minLength;
  // Room on the right of the field for the hint and the clear button
  let searchPadding = 'tw-pr-4';
  if (searchTooShort) searchPadding = 'tw-pr-40';
  else if (search.value) searchPadding = 'tw-pr-10';

  return (
    <div className="tw-space-y-3 tw-p-3 sm:tw-p-4">
      {/* The field keeps a usable width; a long switch moves to the next line instead of squeezing it */}
      <div className={`tw-flex tw-flex-col tw-gap-3 ${compact ? '' : 'md:tw-flex-row md:tw-flex-wrap md:tw-items-center'}`}>
        <div className={`tw-relative tw-min-w-0 ${compact ? '' : 'md:tw-min-w-[15rem] md:tw-max-w-md md:tw-flex-1'}`}>
          <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
          <input
            type="text"
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && search.value) {
                event.preventDefault();
                search.onChange('');
              }
            }}
            placeholder={compact && search.placeholderShort ? search.placeholderShort : search.placeholder}
            aria-label={search.label}
            enterKeyHint="search"
            className={`${GLASS_FIELD} tw-pl-10 ${searchPadding}`}
          />
          <div className="tw-absolute tw-right-1.5 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-items-center tw-gap-1">
            {searchTooShort && (
              <span className="tw-pointer-events-none tw-whitespace-nowrap tw-rounded-full tw-bg-amber-400/15 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-amber-700 dark:tw-text-amber-300">
                {t('dataTable.search.minChars')}
              </span>
            )}
            {search.value && (
              <button
                type="button"
                onClick={() => search.onChange('')}
                aria-label={t('dataTable.search.clear')}
                className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
              >
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Wraps when a long switch (e.g. five years) leaves no room for the button */}
        <div className={`tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 ${compact ? '' : 'md:tw-ml-auto md:tw-justify-end'}`}>
          {status ? (
            <SegmentedControl
              options={status.options}
              value={status.value}
              onChange={status.onChange}
              layoutId={status.layoutId}
              label={status.label || t('dataTable.statusLabel')}
              size={isWide && !compact ? 'md' : 'sm'}
            />
          ) : <span aria-hidden="true" className="tw-flex-1" />}
          {compact ? (
            <Button
              onClick={onOpenFilters}
              aria-label={chips.length ? t('dataTable.filtersOpen', { count: chips.length }) : t('dataTable.filters')}
            >
              <LuListFilter aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-500 dark:tw-text-slate-400" />
              {t('dataTable.filters')}
              {chips.length > 0 && (
                <span className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-teal-500 tw-px-1.5 tw-text-[11px] tw-font-bold tw-leading-none tw-text-white tw-tabular-nums">
                  {chips.length}
                </span>
              )}
            </Button>
          ) : (
            <ColumnMenu {...columnMenu} />
          )}
        </div>
      </div>

      {extra && <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{extra}</div>}

      {chips.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{t('dataTable.filters')}</span>
          {chips.map((chip) => (
            <span
              key={chip.key}
              title={chip.state === 'pending' ? t('dataTable.pendingFilter') : undefined}
              className={`tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-py-1 tw-pl-3 tw-pr-1 tw-text-xs ${CHIP_STATES[chip.state] || CHIP_STATES.applied}`}
            >
              <span className="tw-font-medium tw-opacity-75">{chip.label}:</span>
              <span className="tw-max-w-[12rem] tw-truncate tw-font-semibold">{chip.value}</span>
              <button
                type="button"
                onClick={() => onRemoveChip(chip.key)}
                aria-label={t('dataTable.removeFilter', { label: chip.label })}
                className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors hover:tw-bg-slate-900/10 dark:hover:tw-bg-white/15"
              >
                <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={onClearAll}
            className="tw-rounded-full tw-bg-transparent tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-800 dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.07] dark:hover:tw-text-white"
          >
            {t('dataTable.clearAll')}
          </button>
        </div>
      )}
    </div>
  );
};

DataToolbar.propTypes = {
  search: PropTypes.shape({
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
    placeholderShort: PropTypes.string,
    label: PropTypes.string.isRequired,
    minLength: PropTypes.number,
  }).isRequired,
  status: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.node })).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    layoutId: PropTypes.string.isRequired,
    label: PropTypes.string,
  }),
  columnMenu: PropTypes.object.isRequired,
  compact: PropTypes.bool,
  onOpenFilters: PropTypes.func,
  chips: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    state: PropTypes.string,
  })).isRequired,
  onRemoveChip: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  extra: PropTypes.node,
};

export default DataToolbar;
